import { useState, useEffect } from 'react';
import { adminApi } from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { formatCurrency, formatRelativeTime, getStatusStyle, getTransactionStyle } from '../../lib/utils';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Search, ArrowDownLeft, ArrowUpRight, Filter } from 'lucide-react';

const AdminTransactionsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const res = await adminApi.getTransactions({ limit: 100 });
      setTransactions(res.data);
    } catch (error) {
      toast.error('Failed to load transactions');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTransactions = transactions.filter(tx => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      tx.counterparty_name?.toLowerCase().includes(query) ||
      tx.transaction_id?.toLowerCase().includes(query)
    );
  });

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Transactions
            </h1>
            <p className="text-muted-foreground">Monitor all platform transactions</p>
          </div>
        </div>
        
        {/* Search */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search transactions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-secondary border-white/10"
              data-testid="transaction-search"
            />
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card border border-white/10 rounded-xl p-4">
            <p className="text-2xl font-bold">{transactions.length}</p>
            <p className="text-sm text-muted-foreground">Total Transactions</p>
          </div>
          <div className="bg-card border border-white/10 rounded-xl p-4">
            <p className="text-2xl font-bold text-green-500">
              {transactions.filter(t => t.status === 'completed').length}
            </p>
            <p className="text-sm text-muted-foreground">Completed</p>
          </div>
          <div className="bg-card border border-white/10 rounded-xl p-4">
            <p className="text-2xl font-bold text-yellow-500">
              {transactions.filter(t => t.status === 'pending' || t.status === 'processing').length}
            </p>
            <p className="text-sm text-muted-foreground">Pending</p>
          </div>
          <div className="bg-card border border-white/10 rounded-xl p-4">
            <p className="text-2xl font-bold text-red-500">
              {transactions.filter(t => t.status === 'failed').length}
            </p>
            <p className="text-sm text-muted-foreground">Failed</p>
          </div>
        </div>
        
        {/* Transactions Table */}
        <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
          {isLoading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Type</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Counterparty</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Amount</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Source</th>
                    <th className="text-left p-4 text-sm font-medium text-muted-foreground">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((tx) => {
                    const style = getTransactionStyle(tx.type, tx.direction);
                    const statusStyle = getStatusStyle(tx.status);
                    const Icon = tx.direction === 'in' ? ArrowDownLeft : ArrowUpRight;
                    
                    return (
                      <tr 
                        key={tx.transaction_id} 
                        className="border-b border-white/10 hover:bg-white/5"
                        data-testid={`tx-${tx.transaction_id}`}
                      >
                        <td className="p-4">
                          <div className={`w-8 h-8 rounded-full ${style.bgColor} flex items-center justify-center`}>
                            <Icon className={`w-4 h-4 ${style.color}`} />
                          </div>
                        </td>
                        <td className="p-4">
                          <p className="font-medium">{tx.counterparty_name}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {tx.transaction_id}
                          </p>
                        </td>
                        <td className={`p-4 font-medium ${tx.direction === 'in' ? 'text-green-500' : ''}`}>
                          {tx.direction === 'in' ? '+' : '-'}{formatCurrency(tx.amount)}
                        </td>
                        <td className="p-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>
                            {statusStyle.label}
                          </span>
                        </td>
                        <td className="p-4 capitalize text-sm">
                          {tx.source_type?.replace('_', ' ')}
                        </td>
                        <td className="p-4 text-sm text-muted-foreground">
                          {formatRelativeTime(tx.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminTransactionsPage;
